import { Grid } from "@material-ui/core";
import EmptyPlaceholder from "components/EmptyPlaceholder/EmptyPlaceholder";
import { SlaComplianceStatus, SlaComplianceStatusLabels } from "features/activity/complianceConsts";
import { useGetLastCycleInstancesDataQuery, useGetLastCycleLotsComplianceQuery } from "features/clients/clientConfigs/washes/washProgramSlice";
import WidgetTitle from "features/landing/components/FleetManagementDashboard/WidgetTitle/WidgetTitle";
import ServiceListDialog from "features/vms/vendorDashboard/ServicesGraphWidget/ServiceListDialog";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import FilterService from "services/filterService";
import { theme } from "styles/theme";
import { NewWashDashboardStyle } from "../NewWashDashboardStyle";

const ComplianceByLotWidget = ({ lotsWithWashRule, lots, filterConfigs }) => {
    const classes = NewWashDashboardStyle();
    const { locationFilters } = useSelector(state => state.washDashboardState);

    let { data: lastCycleComplianceForLots, error: lastCycleComplianceError, isLoading: isLoadingLastCycleCompliance } = useGetLastCycleLotsComplianceQuery(locationFilters?.clientId?.value || 0);
    let { data: lastCycleInstancesData, error: lastCycleInstancesError, isLoading: isLoadingInstancesData } = useGetLastCycleInstancesDataQuery(locationFilters?.clientId?.value || 0);

    lastCycleInstancesData = lastCycleInstancesData || [];
    lastCycleInstancesData = lastCycleInstancesData || [];

    const lotsLookup = useMemo(() =>
        lots?.reduce((acc, lot) => { acc[lot.id] = lot; return acc; }, {}),
        [lots]
    );

    var isLoading = isLoadingLastCycleCompliance || isLoadingInstancesData;

    const mappedCycleData = lastCycleInstancesData?.map(data => ({
        ...data?.cycleInstanceData,
        ...data?.activityLog,
        activityStatus: data?.activityLog?.activityId,
        slaStatus: data?.cycleInstanceData?.status,
        slaStatusStr: SlaComplianceStatusLabels[data?.cycleInstanceData?.status] || 'Unknown',
        lotFilter: [
            ...(lotsLookup[data?.cycleInstanceData?.lotId]?.ownedByTenant ? ['tenant'] : []),
            ...(lotsWithWashRule.some(l => l.lotId === data?.cycleInstanceData?.lotId) ? ['washRule'] : [])
        ],
    })) || [];


    const complianceLookup = useMemo(() => {
        if (!lastCycleComplianceForLots) return {};
        return lastCycleComplianceForLots.reduce((acc, c) => {
            acc[c.lotId] = c;
            return acc;
        }, {});
    }, [lastCycleComplianceForLots]);

    const mappedChartData = useMemo(() => {
        if (!lots) return [];
        return lots
            .map(lot => {
                const compliance = complianceLookup[lot.id];
                return compliance ? {
                    lotId: lot.id,
                    name: lot.name,
                    compliance: compliance.slaCompliance,
                    lotFilter: [
                        ...(lotsLookup[lot.id]?.ownedByTenant ? ['tenant'] : []),
                        ...(lotsWithWashRule.some(l => l.lotId === lot.id) ? ['washRule'] : [])
                    ],
                } : null;
            })
            .filter(Boolean);
    }, [lots, complianceLookup]);

    const lotFilters = { ...locationFilters };
    if (lotFilters.lotFilter?.value === 'all') {
        lotFilters.lotFilter = { value: null };
    }

    let filteredCycleData = FilterService.filter(lotFilters, null, mappedCycleData, filterConfigs);
    let filteredChartData = FilterService.filter(lotFilters, null, mappedChartData, filterConfigs);

    const CustomTooltip = ({ payload }) => {
        if (!payload?.length) return null;
        return (
            <div className={classes.graphTooltip}>
                <p>
                    <div style={{ fontSize: 12 }}>
                        <div><b>Lot:</b> {payload[0]?.payload?.name}</div>
                        <div style={{ color: theme.palette.primary.main }}><b>{payload[0]?.payload?.compliance}% compliance</b></div>
                    </div>
                </p>
            </div>
        );
    };

    const withinSlaColumn = (status) => {
        return status === 0 ? <></> :
            <div className={`${classes.withinSlaColumn} ${status === SlaComplianceStatus.WITHIN_SLA ? classes.withinSla : status === SlaComplianceStatus.ABOVE_SLA ? classes.aboveSla : classes.excludedSla}`}>
                {SlaComplianceStatusLabels[status]}
            </div>
    }

    return (
        <Grid container direction="column">
            <Grid container direction='row'>
                <Grid item xs={11}>
                    <WidgetTitle
                        title="Compliance by Lot"
                        loading={isLoading}
                    />
                </Grid>
                <Grid item xs={1} className={classes.exceptionTableIconWrapper}>
                    <ServiceListDialog
                        activities={filteredCycleData}
                        title={'Included Activities'}
                        customColumns={[{
                            name: 'Within SLA',
                            key: 'slaStatus',
                            strKey: 'slaStatusStr',
                            component: (row) => withinSlaColumn(row?.['slaStatus'])
                        }]}
                    />
                </Grid>
            </Grid>
            <Grid item style={{ height: 250, paddingRight: '0.5rem' }}>
                {!!filteredChartData?.length ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={filteredChartData}
                            margin={{ top: 30, right: 50, left: 10, bottom: 5 }}
                        >
                            <XAxis
                                type="category"
                                axisLine={false}
                                dataKey="name"
                                tickLine={false}
                                tick={{
                                    color: theme.palette.primary.main,
                                    fontSize: '0.8em',
                                    fontWeight: '500',
                                    dx: -4,
                                }}
                                angle={-90}
                                textAnchor="end"
                                height={50}
                                allowDataOverflow={false}
                            />
                            <YAxis
                                type="number"
                                domain={[0, 100]}
                                axisLine={false}
                                tickLine={false}
                                tickFormatter={tick => `${tick}%`}
                            />
                            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F3F5F6' }} />
                            <Bar dataKey="compliance" fill={theme.palette.primary.main} maxBarSize={10}>
                                <LabelList dataKey="compliance" position="top" formatter={value => `${value}%`} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <Grid container alignItems="center" justifyContent="center" style={{ height: '100%' }}>
                        <EmptyPlaceholder text="No Compliance Data" />
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

export default ComplianceByLotWidget;
